import { Validator } from './validation/validator.util';
import { ValidatorInput } from './validation/validation-descriptor.entity';
import { ValidationError } from './validation/validation.error';
import { ValidationChecks } from './validation/validation-checks.enum';
import { PipeFunctions } from '../pipes.module/_pipe.functions';
import { AppConstants } from '../app-constants.service';
import {
    Auftraggeber,
    BuergschaftsantragRequest,
    Buergschein,
    BuergscheinLight,
    PartnerProfilData,
    UserData
} from '../../generated/api/kreditportalBackendAPI.schemas';

interface ClearanceDeps {
    fieldName: string;
    valueCondition: string;
    clearFields: string[];
}

export class CreateBuergscheinModel {

    static translate(value: string): string {
        if (value in CreateBuergscheinModel.VALUE_TRANSLATE_MAP) {
            return CreateBuergscheinModel.VALUE_TRANSLATE_MAP[value];
        }

        return value;
    }

    static readonly AUFTRAGGEBER_NAME_OHNE_SP_REGEX = /^[ äöüÄÖÜß[[:punct:]]0-9a-zA-Z]{2,90}$/;
    static readonly AUFTRAGGEBER_NAME_MIT_SP_REGEX = /^.{2,80}$/;

    static readonly AUFTRAGGEBER_STRASSE_OHNE_SP_REGEX = /^[ äöüÄÖÜß[[:punct:]]0-9a-zA-Z]{0,60}$/;
    static readonly AUFTRAGGEBER_STRASSE_MIT_SP_REGEX = /^.{0,41}$/;

    static readonly AUFTRAGGEBER_PLZ_REGEX = /^[\d\w \-]{0,8}$/;

    static readonly AUFTRAGGEBER_ORT_OHNE_SP_REGEX = /^.{2,21}$/;
    static readonly AUFTRAGGEBER_ORT_MIT_SP_REGEX = /^.{2,30}$/;

    static readonly PLZ_ORT_REGEX = /^((?:[A-Z]{1,2}-)?\d{4,5}) (.+)$/i;

    static readonly VALUE_TRANSLATE_MAP = {
        U: 'Neuantrag',
        N: 'Änderungsantrag',

        MA: 'Mängelansprüchebürgschaft',
        VE: 'Vertragserfüllungsbürgschaft',
        AF: 'Ausführungsbürgschaft',
        BS: 'Bauhandwerkersicherungsbürgschaft (gem. §650f BGB)',
        VA: 'Vorauszahlungsbürgschaft',
        BI: 'Bietungsbürgschaft',

        unbefristet: 'nicht zu befristen',
        befristet: 'zu befristen',

        AV: 'Adresse verfügbar',
        KA: 'Keine Adresse verfügbar',
    };

    static readonly AUFTRAGGEBER_TYPEN = [
        'Firma',
        'Öffentlicher Auftraggeber',
        'Privatperson'
    ];

    private static readonly CLEARANCE_DEPS: ClearanceDeps[] = [
        {
            fieldName: 'typeOfAntrag',
            valueCondition: 'Neuantrag',
            clearFields: ['aenderungsAntragBuergscheinNummer'],
        },
        {
            fieldName: 'buergschaftsArt',
            valueCondition: 'AF',
            clearFields: [
                'gewaehrleistungsbuergschaftenDatum',
                'schlussrechnungsDatum',
                'bietungsbuergschaftenDatum',
                'bauhandwerkersicherungssbuergschaftenDatum',
                'vorauszahlungsbuergschaftenDatum',
            ],
        },
        {
            fieldName: 'buergschaftsArt',
            valueCondition: 'VE',
            clearFields: [
                'ausfuehrungsbuergschaftenDatum',
                'gewaehrleistungsbuergschaftenDatum',
                'schlussrechnungsDatum',
                'bietungsbuergschaftenDatum',
                'bauhandwerkersicherungssbuergschaftenDatum',
                'vorauszahlungsbuergschaftenDatum',
            ],
        },
        {
            fieldName: 'buergschaftsArt',
            valueCondition: 'MA',
            clearFields: ['ausfuehrungsbuergschaftenDatum',
                'vertragserfuellungsbuergschaftenDatum',
                'bietungsbuergschaftenDatum',
                'vorauszahlungsbuergschaftenDatum',
                'bauhandwerkersicherungssbuergschaftenDatum',
            ],
        },
        {
            fieldName: 'buergschaftsArt',
            valueCondition: 'BI',
            clearFields: [
                'ausfuehrungsbuergschaftenDatum',
                'vertragserfuellungsbuergschaftenDatum',
                'gewaehrleistungsbuergschaftenDatum',
                'schlussrechnungsDatum',
                'vorauszahlungsbuergschaftenDatum',
                'bauhandwerkersicherungssbuergschaftenDatum',
                'gewaehrleistungsfrist',
                'gewaehrleistungsfristJahre',
                'gewaehrleistungsfristMonate',
            ],
        },
        {
            fieldName: 'buergschaftsArt',
            valueCondition: 'BS',
            clearFields: [
                'ausfuehrungsbuergschaftenDatum',
                'vertragserfuellungsbuergschaftenDatum',
                'gewaehrleistungsbuergschaftenDatum',
                'schlussrechnungsDatum',
                'bietungsbuergschaftenDatum',
                'vorauszahlungsbuergschaftenDatum',
                'gewaehrleistungsfrist',
                'gewaehrleistungsfristJahre',
                'gewaehrleistungsfristMonate',
            ],
        },
        {
            fieldName: 'buergschaftsArt',
            valueCondition: 'VA',
            clearFields: [
                'ausfuehrungsbuergschaftenDatum',
                'vertragserfuellungsbuergschaftenDatum',
                'gewaehrleistungsbuergschaftenDatum',
                'schlussrechnungsDatum',
                'bietungsbuergschaftenDatum',
                'bauhandwerkersicherungssbuergschaftenDatum',
                'gewaehrleistungsfrist',
                'gewaehrleistungsfristJahre',
                'gewaehrleistungsfristMonate',
            ],
        },
        {
            fieldName: 'gewaehrleistungsfrist',
            valueCondition: '4',
            clearFields: ['gewaehrleistungsfristJahre', 'gewaehrleistungsfristMonate'],
        },
        {
            fieldName: 'gewaehrleistungsfrist',
            valueCondition: '5',
            clearFields: ['gewaehrleistungsfristJahre', 'gewaehrleistungsfristMonate'],
        },
    ];

    isDigitaleBuergschaft = false;

    vertrittOeffentlicheRessorts = false;

    auftraggeberSpKennung: string;

    templateSpBeschreibung: string;

    moechteAbweichendenBuergschaftstext: boolean;

    vertretungsformelChecked = false;

    versandartAuswahlAvailable = false;

    forcedDigitalAuftraggebers: Auftraggeber[] = [];

    // Für den Upload relevant
    user: string;
    roleName: string;
    activeUser: string;
    files = [];

    firma = new ValidatorInput(
        'firma',
        '',
        'Firma',
        /^.{2,79}$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    vorname = new ValidatorInput(
        'vorname',
        '',
        'Vorname',
        /^\D{0,79}$/,
        null,
        false,
        ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    nachname = new ValidatorInput(
        'nachname',
        '',
        'Nachname',
        /^\D{0,79}$/,
        null,
        false,
        ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    street = new ValidatorInput(
        'street',
        '',
        'Straße, Hausnr. / Postfach',
        /^.{0,79}$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    plz = new ValidatorInput(
        'plz',
        '',
        'PLZ',
        /^\d{5}$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    ort = new ValidatorInput(
        'ort',
        '',
        'Ort',
        /^.{2,79}$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    vorwahl = new ValidatorInput(
        'vorwahl',
        '',
        'Vorwahl',
        /^.*$/,
        null,
        false,
        0,
        this.checkVorwahl.bind(this)
    );

    rufnummer = new ValidatorInput(
        'rufnummer',
        '',
        'Rufnummer',
        /^.*$/,
        null,
        false,
        0,
        this.checkRufnummer.bind(this)
    );

    email = new ValidatorInput(
        'email',
        '',
        'Email',
        AppConstants.VHV_EMAIL_REGEX,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
        this.checkEmail.bind(this)
    );

    vertrag = new ValidatorInput(
        'vertrag',
        '',
        'Vertragsnummer',
        /^\d{2,3}-\d{6}$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK | ValidationChecks.SELECT_BOX_SELECTED_CHECK,
    );

    typeOfAntrag = new ValidatorInput(
        'typeOfAntrag',
        'U',
        'Art des Antrages',
        /^[UN]$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    aenderungsAntragBuergscheinNummer = new ValidatorInput(
        'aenderungsAntragBuergscheinNummer',
        '',
        'Bürgscheinnummer',
        /^\d?\d?\d-\d\d\/\d?\d$/,
        'Bitte geben Sie eine Bürgscheinnummer im Format nnn-nn/nn ein.',
        true,
        0,
        this.checkBuergscheinnummer4AenderungsAntrag.bind(this)
    );

    auftraggeberTyp = new ValidatorInput(
        'auftraggeberTyp',
        'Firma',
        'Auftraggeber-Typ',
        /^(?:Firma|Öffentlicher Auftraggeber|Privatperson)$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    auftraggeberName = new ValidatorInput(
        'auftraggeberName',
        '',
        'Name des Auftraggebers',
        CreateBuergscheinModel.AUFTRAGGEBER_NAME_OHNE_SP_REGEX,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    auftraggeberStrasseNr = new ValidatorInput(
        'auftraggeberStrasseNr',
        '',
        'Straße und Hausnummer',
        CreateBuergscheinModel.AUFTRAGGEBER_STRASSE_OHNE_SP_REGEX,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    auftraggeberPlz = new ValidatorInput(
        'auftraggeberPlz',
        '',
        'PLZ',
        CreateBuergscheinModel.AUFTRAGGEBER_PLZ_REGEX,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    auftraggeberOrt = new ValidatorInput(
        'auftraggeberOrt',
        '',
        'Ort',
        CreateBuergscheinModel.AUFTRAGGEBER_ORT_OHNE_SP_REGEX,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    auftraggeberLand = new ValidatorInput(
        'auftraggeberLand',
        'DE',
        'Land',
        /^[A-Z]{1,3}$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.SELECT_BOX_SELECTED_CHECK,
    );

    vertretungsformel1 = new ValidatorInput(
        'vertretungsformel1',
        '',
        'Hauptauftraggeber',
        /^.*$/,
        null,
        false,
        0,
        this.checkVertretungsformel.bind(this)
    );

    vertretungsformel2 = new ValidatorInput(
        'vertretungsformel2',
        '',
        'Vertreten durch 1',
        /^.*$/,
        null,
        false,
        ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    vertretungsformel3 = new ValidatorInput(
        'vertretungsformel3',
        '',
        'Vertreten durch 2',
        /^.*$/,
        null,
        false,
        ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    vertretungsformel4 = new ValidatorInput(
        'vertretungsformel4',
        '',
        'Vertreten durch 3',
        /^.*$/,
        null,
        false,
        ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    vertretungsformel5 = new ValidatorInput(
        'vertretungsformel5',
        '',
        'Vertreten durch 4',
        /^.*$/,
        null,
        false,
        ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    forcedAuftraggeberValidator = new ValidatorInput(
        'forcedAuftraggeberValidator',
        '',
        'Bestätigung Nichtnutzung Digitaler Auftraggeber',
        /^.*$/,
        null,
        false,
        0,
        this.checkForcedAuftraggeberValidator.bind(this)
    );

    buergschaftsArt = new ValidatorInput(
        'buergschaftsArt',
        '',
        'Bürgschaftsart',
        /^(?:AF|VE|MA|BI|BS|VA)$/,
        null,
        false,
        0,
        this.checkBuergschaftsArt.bind(this)
    );

    ausfuehrungsbuergschaftenDatum = new ValidatorInput(
        'ausfuehrungsbuergschaftenDatum',
        '',
        'Vorauss. Datum der Fertigstellung',
        /^.*$/,
        null,
        false,
        0,
        this.checkBuergschaftsDate.bind(this)
    );

    vertragserfuellungsbuergschaftenDatum = new ValidatorInput(
        'vertragserfuellungsbuergschaftenDatum',
        '',
        'Vorauss. Datum der Fertigstellung',
        /^.*$/,
        null,
        false,
        0,
        this.checkBuergschaftsDate.bind(this)
    );

    gewaehrleistungsbuergschaftenDatum = new ValidatorInput(
        'gewaehrleistungsbuergschaftenDatum',
        '',
        'Vorauss. Datum der Fertigstellung',
        /^.*$/,
        null,
        false,
        0,
        this.checkBuergschaftsDate.bind(this)
    );

    schlussrechnungsDatum = new ValidatorInput(
        'schlussrechnungsDatum',
        '',
        'Schlussrechnungs-Datum',
        /^.*$/,
        null,
        false,
        0,
        this.checkBuergschaftsDate.bind(this)
    );

    bietungsbuergschaftenDatum = new ValidatorInput(
        'bietungsbuergschaftenDatum',
        '',
        'Vorauss. Datum der Fertigstellung',
        /^.*$/,
        null,
        false,
        0,
        this.checkBuergschaftsDate.bind(this)
    );

    bauhandwerkersicherungssbuergschaftenDatum = new ValidatorInput(
        'bauhandwerkersicherungssbuergschaftenDatum',
        '',
        'Vorauss. Datum der Fertigstellung',
        /^.*$/,
        null,
        false,
        0,
        this.checkBuergschaftsDate.bind(this)
    );

    vorauszahlungsbuergschaftenDatum = new ValidatorInput(
        'vorauszahlungsbuergschaftenDatum',
        '',
        'Vorauss. Datum der Fertigstellung',
        /^.*$/,
        null,
        false,
        0,
        this.checkBuergschaftsDate.bind(this)
    );

    vertragsDatum = new ValidatorInput(
        'vertragsDatum',
        '',
        'Vertrag/Auftrag vom',
        /^.*$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK,
        this.checkDateFieldPast.bind(this)
    );

    vertragsnummer = new ValidatorInput(
        'vertragsnummer',
        '',
        'Vertrags-/Auftragsnummer',
        /^[ A-Za-z0-9äöüÄÖÜß.\-,_\/\\]{0,30}$/,
        'Geben Sie bitte eine gültige Vertragsnummer ein. Zugelassene Zeichen: Buchstaben, Ziffern, Leerzeichen, Sonderzeichen: . - , _ / \\',
        true,
        ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    buergschaftshoehe = new ValidatorInput(
        'buergschaftshoehe',
        '',
        'Bürgschaftshöhe',
        /^\d{1,9}(,\d{2})?$/,
        'Geben Sie bitte einen numerischen Wert (max. 9-stellig) ohne Tausendertrennzeichen ein. Optional können zwei Nachkommastellen angegeben werden.',
        true,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    prozentSatz = new ValidatorInput(
        'prozentSatz',
        '',
        'Prozentsatz',
        /^\d{1,3}(,\d{1,2})?$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK | ValidationChecks.CHECK_PERCENT,
    );

    frist = new ValidatorInput(
        'frist',
        'unbefristet',
        'Befristung',
        /^(?:unbefristet|befristet)$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    fristDatum = new ValidatorInput(
        'fristDatum',
        '',
        'Befristungsdatum',
        /^.*$/,
        null,
        false,
        0,
        this.checkBuergschaftsFrist.bind(this)
    );

    gesetzlGrundlage = new ValidatorInput(
        'gesetzlGrundlage',
        'VOB',
        'Frist',
        /^(?:VOB|BGB)$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    checkboxHatBesondereHinweise = new ValidatorInput(
        'checkboxHatBesondereHinweise',
        '',
        '',
        /^.*$/,
        null,
        false,
        0
    );

    besondereHinweise = new ValidatorInput(
        'besondereHinweise',
        '',
        'Besondere Hinweise',
        /^[ äöüÄÖÜß[[:punct:]]0-9a-zA-Z]{0,70}$/,
        null,
        false,
        0,
        this.checkBesondereHinweise.bind(this)
    );

    auftragsortGewerk = new ValidatorInput(
        'auftragsortGewerk',
        '',
        'Gewerk / Art der Arbeit',
        /^[ äöüÄÖÜß[[:punct:]]0-9a-zA-Z]{0,240}$/i,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    auftragsortVerfuegbar = new ValidatorInput(
        'auftragsortVerfuegbar',
        'AV',
        'Ort der Arbeit',
        /^(?:AV|KA)$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    auftragsortBeschreibung = new ValidatorInput(
        'auftragsortBeschreibung',
        '',
        'Beschreibung des Ortes',
        /^.{0,60}$/,
        null,
        false,
        0,
        this.checkArbeitsort.bind(this)
    );

    auftragsortStrasseNr = new ValidatorInput(
        'auftragsortStrasseNr',
        '',
        'Straße, Hausnummer / Postfach',
        /^[ äöüÄÖÜß[[:punct:]]0-9a-zA-Z]{2,60}$/,
        null,
        false,
        0,
        this.checkArbeitsort.bind(this)
    );

    auftragsortPlz = new ValidatorInput(
        'auftragsortPlz',
        '',
        'PLZ',
        /^[A-Z0-9\-]{0,8}$/,
        null,
        false,
        0,
        this.checkArbeitsort.bind(this)
    );

    auftragsortOrt = new ValidatorInput(
        'auftragsortOrt',
        '',
        'Ort',
        /^[ äöüÄÖÜß[[:punct:]]0-9a-zA-Z]{0,60}$/,
        null,
        false,
        0,
        this.checkArbeitsort.bind(this)
    );

    buergschaftsformat = new ValidatorInput(
        'buergschaftsformat',
        'PAPIER',
        'Bürgschaftsformat',
        /^(?:PDF|PAPIER)$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    checkboxErklaerungZustimmung = new ValidatorInput(
        'checkboxErklaerungZustimmung',
        '',
        'Zustimmung der Erklärung',
        /^zugestimmt$/,
        null,
        true,
        0,
        this.checkCheckboxErklaerungZugestimmt.bind(this)
    );

    erklaerungsDatum = new ValidatorInput(
        'erklaerungsDatum',
        new Date().toLocaleDateString(),
        'Datum',
        /^.*$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK,
    );

    erklaerungsOrt = new ValidatorInput(
        'erklaerungsOrt',
        '',
        'Ort der Erklärung',
        /^.{2,79}$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    erklaerungsName = new ValidatorInput(
        'erklaerungsName',
        '',
        'Vor- und Nachname des Erklärenden',
        /^\D{2,79}$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CUSTOM_REGEX_CHECK,
    );

    templateSpKennung = new ValidatorInput(
        'templateSpKennung',
        '',
        'Bürgschaftstext',
        /^.*$/,
        null
    );

    gewuenschtesFormular = new ValidatorInput(
        'gewuenschtesFormular',
        '',
        'Gewünschtes Formular',
        /^(?:NEIN|SF00|SF01|ST10|ST20|AG03|EF13|EF21|AG38|KF12|KF23|KF10|VO30)$/,
        null,
        false,
        0,
        this.checkGewuenschtesFormular.bind(this)
    );

    gewaehrleistungsfrist = new ValidatorInput(
        'gewaehrleistungsfrist',
        '',
        'Gewährleistungsfrist',
        /^(?:4|5|beträgt)$/,
        null,
        false,
        0,
        this.checkMAFrist.bind(this)
    );

    gewaehrleistungsfristJahre = new ValidatorInput(
        'gewaehrleistungsfristJahre',
        '',
        'Jahre',
        /^\d+$/,
        null,
        false,
        0,
        this.checkBuergschaftsFristJahre.bind(this)
    );

    gewaehrleistungsfristMonate = new ValidatorInput(
        'gewaehrleistungsfristMonate',
        '',
        'Monate',
        /^\d+$/,
        null,
        false,
        0,
        this.checkBuergschaftsFristMonate.bind(this)
    );

    checkboxHinterlegung = new ValidatorInput(
        'checkboxHinterlegung',
        '',
        '',
        /^.*$/,
        null,
        false,
        0
    );

    checkboxBGB768 = new ValidatorInput(
        'checkboxBGB768',
        '',
        '',
        /^.*$/,
        null,
        false,
        0
    );

    checkboxSGBVII150Abs3 = new ValidatorInput(
        'checkboxSGBVII150Abs3',
        '',
        '',
        /^.*$/,
        null,
        false,
        0
    );

    checkboxSGBIV28eAbs3a = new ValidatorInput(
        'checkboxSGBIV28eAbs3a',
        '',
        '',
        /^.*$/,
        null,
        false,
        0
    );

    checkboxSicherheitseinbehalt = new ValidatorInput(
        'checkboxSicherheitseinbehalt',
        '',
        '',
        /^.*$/,
        null,
        false,
        0
    );

    checkboxAentG14 = new ValidatorInput(
        'checkboxAwntG14',
        '',
        '',
        /^.*$/,
        null,
        false,
        0
    );

    /**
     * Nur für Felder relevant, die wirklich validiert werden sollen
     */
    collection: ValidatorInput[] = [
        // Seite 1
        this.firma,
        this.vorname,
        this.nachname,
        this.street,
        this.plz,
        this.ort,
        this.vorwahl,
        this.rufnummer,
        this.email,
        this.vertrag,

        // Seite 2
        this.typeOfAntrag,
        this.aenderungsAntragBuergscheinNummer,
        this.auftraggeberTyp,
        this.auftraggeberName,
        this.auftraggeberStrasseNr,
        this.auftraggeberPlz,
        this.auftraggeberOrt,
        this.auftraggeberLand,
        this.vertretungsformel1,
        this.vertretungsformel2,
        this.vertretungsformel3,
        this.vertretungsformel4,
        this.vertretungsformel5,
        this.forcedAuftraggeberValidator,

        // Seite 3
        this.buergschaftsArt,
        this.ausfuehrungsbuergschaftenDatum,
        this.vertragserfuellungsbuergschaftenDatum,
        this.gewaehrleistungsbuergschaftenDatum,
        this.schlussrechnungsDatum,
        this.bietungsbuergschaftenDatum,
        this.bauhandwerkersicherungssbuergschaftenDatum,
        this.vorauszahlungsbuergschaftenDatum,
        this.buergschaftshoehe,
        this.prozentSatz,
        this.frist,
        this.fristDatum,
        this.gesetzlGrundlage,
        this.templateSpKennung,
        this.gewuenschtesFormular,
        this.gewaehrleistungsfrist,
        this.gewaehrleistungsfristJahre,
        this.gewaehrleistungsfristMonate,
        this.checkboxHatBesondereHinweise,
        this.besondereHinweise,
        this.vertragsDatum,
        this.vertragsnummer,
        this.auftragsortVerfuegbar,
        this.auftragsortBeschreibung,
        this.auftragsortGewerk,
        this.auftragsortStrasseNr,
        this.auftragsortPlz,
        this.auftragsortOrt,
        this.buergschaftsformat,

        // Seite 4
        this.checkboxErklaerungZustimmung,
        this.erklaerungsOrt,
        this.erklaerungsName,
    ];

    public validate(untilName = 'never stop(dummy)'): ValidationError[] {
        const result: ValidationError[] = [];
        for (const field of this.collection) {
            field.valid = Validator.doValidate(field, 1, result);
            if (field.name === untilName) {
                break;
            }
        }
        return result.length === 0 ? null : result;
    }

    public validateField(field: ValidatorInput): boolean {
        return field.valid = Validator.doValidate(field, 1, []);
    }

    // Ort von Auftraggeberzeile ermitteln, sonst blank
    public getOrtFromAuftraggeber(auftraggeberZeile: string): string {
        if (this.getIndexOfPlzOrtTrenner(auftraggeberZeile) >= 0) {
            return auftraggeberZeile.substr(this.getIndexOfPlzOrtTrenner(auftraggeberZeile) + 1);
        }

        return '';
    }

    private checkBuergscheinnummer4AenderungsAntrag(): void {
        this.aenderungsAntragBuergscheinNummer.valid = true;

        if (this.typeOfAntrag.value === 'N') { // Urschrift
            if (!this.aenderungsAntragBuergscheinNummer.regex.test(this.aenderungsAntragBuergscheinNummer.value)) {
                this.aenderungsAntragBuergscheinNummer.valid = false;
            }
        }
    }

    private checkVertretungsformel(field: ValidatorInput): void {
        field.valid = true;
        field.errorMsg = '';

        if (this.shouldNotApplyVertretungsformel()) {
            return;
        }

        if (Validator.isEmpty(field)) {
            field.valid = false;
            field.errorMsg = Validator.MSG_ON_REQUIRED_BUT_EMPTY.replace('$_label', field.label);
        }
    }

    private checkForcedAuftraggeberValidator(field: ValidatorInput): void {
        field.valid = true;
        field.errorMsg = '';

        // Wenn kein forcierter digitaler AG verfügbar, dann muss auch nichts bestätigt werden
        if (!this.forcedDigitalAuftraggebers || this.forcedDigitalAuftraggebers.length === 0) {
            return;
        }

        // Ansonsten muss die Bestätigung zur (Nicht)Nutzung digitaler Auftraggeber gesetzt sein
        if (!field.value) {
            field.valid = false;
            field.errorMsg = 'Bitte bestätigen Sie die Nichtnutzung des digitalen Auftraggebers.';
        }
    }

    private checkBesondereHinweise(field: ValidatorInput): void {
        field.valid = true;

        if (!this.checkboxHatBesondereHinweise.value) {
            field.value = '';

            return;
        }

        if (!Validator.isCustomRegexOk(field)) {
            field.valid = false;
            field.errorMsg = Validator.MSG_ON_REGEX_FAIL
                .replace('$_label', field.label)
                .replace('$_value', field.value);
        }
    }

    private checkArbeitsort(field: ValidatorInput): void {
        field.valid = true;

        if (Validator.isEmpty(field)) {
            field.valid = false;
            field.errorMsg = Validator.MSG_ON_REQUIRED_BUT_EMPTY.replace('$_label', field.label);
        } else if (!Validator.isCustomRegexOk(field)) {
            field.valid = false;
            field.errorMsg = Validator.MSG_ON_REGEX_FAIL
                .replace('$_label', field.label)
                .replace('$_value', field.value);
        }

        if (this.auftragsortVerfuegbar.value === 'AV') {
            this.auftragsortBeschreibung.value = '';
            this.auftragsortBeschreibung.valid = true;
        } else {
            this.auftragsortStrasseNr.value = '';
            this.auftragsortStrasseNr.valid = true;

            this.auftragsortPlz.value = '';
            this.auftragsortPlz.valid = true;

            this.auftragsortOrt.value = '';
            this.auftragsortOrt.valid = true;
        }
    }

    private checkBuergschaftsArt(field: ValidatorInput): void {
        field.valid = true;
        field.errorMsg = '';

        if (Validator.isEmpty(field)) {
            field.valid = false;
            field.errorMsg = Validator.MSG_ON_NONE_SELECTED.replace('$_label', field.label);

            return;
        }

        if (this.shouldNotApplyVertretungsformel()) {
            return;
        }

        if (!(['MA', 'VA', 'VE'].includes(this.buergschaftsArt.value))) {
            field.valid = false;
            field.errorMsg = 'Sie haben angegeben, dass eine Vertretungsformel vorhanden ist. ' +
                'Diese ist nur verfügbar für folgende Bürgschaftsarten: Mängelansprüchebürgschaft, ' +
                'Vertragserfüllungsbürgschaft, Vorauszahlungsbürgschaft';
        }
    }

    private checkBuergschaftsDate(field: ValidatorInput): void {
        field.valid = true;
        this.schlussrechnungsDatum.valid = true;

        switch (this.buergschaftsArt.value) {
            case 'AF':
                if ('ausfuehrungsbuergschaftenDatum' === field.name) {
                    this.checkDateFieldAll(field);
                }
                break;
            case 'VE':
                if ('vertragserfuellungsbuergschaftenDatum' === field.name) {
                    this.checkDateFieldAll(field);
                }
                break;
            case 'MA':
                if ('gewaehrleistungsbuergschaftenDatum' === field.name || 'schlussrechnungsDatum' === field.name) {
                    const valid1: boolean = Validator.isDateCorrect(this.gewaehrleistungsbuergschaftenDatum);
                    const valid2: boolean = Validator.isDateCorrect(this.schlussrechnungsDatum);

                    if (valid1 || valid2) {
                        if (!valid2) {
                            this.schlussrechnungsDatum.value = '';
                            this.schlussrechnungsDatum.valid = true;
                        } else if (!valid1) {
                            this.gewaehrleistungsbuergschaftenDatum.value = '';
                            this.gewaehrleistungsbuergschaftenDatum.valid = true;
                        }
                    }
                }
                break;
            case 'BI':
                if ('bietungsbuergschaftenDatum' === field.name) {
                    this.checkDateFieldAll(field);
                }
                break;
            case 'BS':
                if ('bauhandwerkersicherungssbuergschaftenDatum' === field.name) {
                    this.checkDateFieldAll(field);
                }
                break;
            case 'VA':
                if ('vorauszahlungsbuergschaftenDatum' === field.name) {
                    this.checkDateFieldAll(field);
                }
                break;
        }
    }

    private checkBuergschaftsFrist(field: ValidatorInput): void {
        field.valid = true;

        if ('befristet' === this.frist.value) {
            this.checkDateFieldAll(field);
        }
    }

    private checkMAFrist(field: ValidatorInput): void {
        field.valid = true;

        if (['MA', 'AF', 'VE'].includes(this.buergschaftsArt.value)) {
            if (!/^(?:4|5|beträgt)$/.test(field.value)) {
                field.valid = false;
            }
        }
    }

    private checkBuergschaftsFristJahre(field: ValidatorInput): void {
        field.valid = true;
        field.errorMsg = '';

        if ('beträgt' === this.gewaehrleistungsfrist.value) {
            if (field.value.length === 0) {
                field.value = 0;
            }
            if (!field.regex.test(field.value)) {
                field.valid = false;
                field.errorMsg = 'Numerische Eingabe erforderlich. ' + field.value + ' ist keine gültige Jahresangabe.';
            }
        }
    }

    private checkBuergschaftsFristMonate(field: ValidatorInput): void {
        field.valid = true;
        field.errorMsg = '';

        if ('beträgt' === this.gewaehrleistungsfrist.value) {
            if (!field.regex.test(field.value)) {
                field.valid = false;
                field.errorMsg = 'Numerische Eingabe erforderlich. ' + field.value + ' ist keine gültige Monatsangabe.';
            } else if (field.value == 0 && this.gewaehrleistungsfristJahre.value == 0) {
                field.valid = false;
                this.gewaehrleistungsfristJahre.valid = false;
                this.gewaehrleistungsfristJahre.errorMsg = 'Bitte wählen Sie eine Frist größer als 0.';
                field.errorMsg = 'Bitte wählen Sie eine Frist größer als 0.';
            } else {
                field.valid = (field.value >= 0 && field.value < 12);
                field.errorMsg = 'Die Monatsangabe muss zwischen 0 und 11 Monate betragen.';
            }
        }
    }

    private checkDateFieldAll(field: ValidatorInput): boolean {
        field.valid = true;

        if (!Validator.isDateCorrect(field)) {
            field.errorMsg = Validator.MSG_WRONG_DATE_FORMAT;
            field.valid = false;
        } else if (!Validator.isDateInFuture(field)) {
            field.errorMsg = Validator.MSG_DATE_IN_PAST;
            field.valid = false;
        } else if (!Validator.isDateInRange(field)) {
            field.errorMsg = Validator.MSG_DATE_TO_FAR;
            field.valid = false;
        }

        return field.valid;
    }

    private checkDateFieldPast(field: ValidatorInput): boolean {
        field.valid = true;

        if (field.value && field.value.length > 0) {
            if (!Validator.isDateCorrect(field)) {
                field.errorMsg = Validator.MSG_WRONG_DATE_FORMAT;
                field.valid = false;
            } else if (Validator.isDateInFuture(field)) {
                field.errorMsg = Validator.MSG_DATE_IN_FUTURE;
                field.valid = false;
            }
        }

        return field.valid;
    }

    private validateVorwahl(field: ValidatorInput) {
        if (!/^(([+0])[1-9])\d{0,7}\d$/.test(field.value)) {
            field.valid = false;
            field.errorMsg = Validator.MSG_ON_REGEX_FAIL
                .replace('$_label', field.label)
                .replace('$_value', field.value);
        }
    }

    private checkVorwahl(field: ValidatorInput) {
        this.vorwahl.valid = true;
        this.vorwahl.errorMsg = '';
        this.rufnummer.valid = true;
        this.rufnummer.errorMsg = '';

        if (this.vorwahl.value.length > 0) {
            this.validateVorwahl(this.vorwahl);
        }

        if (this.rufnummer.value.length > 0) {
            this.checkVorwahlRufnummer(this.vorwahl, this.rufnummer);
        }

        return field.valid;
    }

    private checkRufnummer(field: ValidatorInput) {
        this.vorwahl.valid = true;
        this.vorwahl.errorMsg = '';
        this.rufnummer.valid = true;
        this.rufnummer.errorMsg = '';

        if (this.vorwahl.value.length > 0 || this.rufnummer.value.length > 0) {
            this.checkVorwahlRufnummer(this.vorwahl, this.rufnummer);
        }

        return field.valid;
    }

    private checkVorwahlRufnummer(vorwahl: ValidatorInput, rufnummer: ValidatorInput) {
        // Rufnummer gefuellt, aber Vorwahl nicht
        if (rufnummer.value.length > 0) {
            if (vorwahl.value.length === 0) {
                vorwahl.valid = false;
                vorwahl.errorMsg = Validator.MSG_VORWAHL_RUFNUMMER;
                rufnummer.valid = false;
                rufnummer.errorMsg = Validator.MSG_VORWAHL_RUFNUMMER;
            }
        } else {
            // Rufnummer nicht gefuellt, aber Vorwahl
            if (vorwahl.value.length > 0) {
                vorwahl.valid = false;
                vorwahl.errorMsg = Validator.MSG_VORWAHL_RUFNUMMER;
                rufnummer.valid = false;
                rufnummer.errorMsg = Validator.MSG_VORWAHL_RUFNUMMER;
            }
        }
    }

    private checkEmail(field: ValidatorInput): boolean {
        field.valid = field.value.length <= AppConstants.VHV_EMAIL_MAXLEN;

        if (!field.valid) {
            field.errorMsg = 'Der Wert \'' + field.value + '\' ist zu lang.';
        }

        return field.valid;
    }

    private checkBuergschaftstext(field: ValidatorInput): boolean {
        field.valid = true;
        field.errorMsg = '';

        // Feld muss nicht ausgefüllt sein, wenn BS analog oder abweichender BS-Text gesetzt ist
        if (!this.auftraggeberSpKennung || this.moechteAbweichendenBuergschaftstext) {
            return true;
        }

        // Ansonsten muss in der Select-Box eine Auswahl getroffen worden sein
        if (Validator.isEmpty(field)) {
            field.valid = false;
            field.errorMsg = Validator.MSG_ON_REQUIRED_BUT_EMPTY.replace('$_label', field.label);

            return false;
        }

        return true;
    }

    private checkGewuenschtesFormular(field: ValidatorInput): boolean {
        field.valid = true;
        field.errorMsg = '';

        // Feld muss nicht ausgefüllt sein wenn Auftraggeber und Trustlog-Template gesetzt
        // und kein abweichender Text gewünscht ist
        if (   this.auftraggeberSpKennung
            && this.templateSpKennung.value
            && !this.moechteAbweichendenBuergschaftstext
        ) {
            return true;
        }

        if (Validator.isEmpty(field)) {
            field.valid = false;
            field.errorMsg = Validator.MSG_ON_REQUIRED_BUT_EMPTY.replace('$_label', field.label);

            return false;
        }

        if (!Validator.isCustomRegexOk(field)) {
            field.valid = false;
            field.errorMsg = Validator.MSG_ON_REGEX_FAIL
                .replace('$_label', field.label)
                .replace('$_value', field.value);

            return false;
        }

        if (this.shouldNotApplyVertretungsformel()) {
            return true;
        }

        if (field.value === 'NEIN') {
            field.valid = false;
            field.errorMsg = 'Sie haben angegeben, dass eine Vertretungsformel vorhanden ist. ' +
                'In diesem Fall kann die Formularart "Formular der VHV" nicht verwendet werden.';

            return false;
        }

        return true;
    }

    private checkCheckboxErklaerungZugestimmt(field: ValidatorInput): void {
        if (field.value === true) {
            field.valid = true;
            field.errorMsg = '';
        } else {
            field.valid = false;
            field.errorMsg = 'Für die Beantragung der Bürgschaft ist die Zustimmung der Erklärung notwendig.';
        }
    }

    public sweepModel() {
        for (const dep of CreateBuergscheinModel.CLEARANCE_DEPS) {
            if (this[dep.fieldName].value !== dep.valueCondition) {
                continue;
            }

            for (const cl of dep.clearFields) {
                (<ValidatorInput>this[cl]).value = '';
            }
        }
    }

    public clearModel(): void {
        // Leere sämtliche Felder
        this.collection.forEach(i => {
            i.value = '';
            i.valid = true;
        });

        this.isDigitaleBuergschaft = false;

        this.auftraggeberSpKennung = undefined;

        this.templateSpBeschreibung = undefined;

        this.moechteAbweichendenBuergschaftstext = undefined;

        this.files = [];

        // Stelle Standardwerte wieder her
        // ODOT Das ist doof implementiert; Vorbelegungen sollten in ein Extra-Feld gesondert zur Belegung. Sonst Doppelpflege hier
        this.typeOfAntrag.value = 'U';
        this.auftraggeberTyp.value = 'Firma';
        this.auftraggeberLand.value = 'DE';
        this.frist.value = 'unbefristet';
        this.gesetzlGrundlage.value = 'VOB';
        this.erklaerungsDatum.value = new Date().toLocaleDateString();
        this.auftragsortVerfuegbar.value = 'AV';
        this.versandartAuswahlAvailable = false;
        this.buergschaftsformat.value = 'PAPIER';
    }

    public forRequest(): BuergschaftsantragRequest {
        const request: BuergschaftsantragRequest = {};

        request.firma = this.firma.value;
        request.vorname = this.vorname.value;
        request.nachname = this.nachname.value;
        request.street = this.street.value;
        request.plz = this.plz.value;
        request.ort = this.ort.value;
        request.vorwahl = this.vorwahl.value;
        request.rufnummer = this.rufnummer.value;
        request.email = this.email.value;
        request.vertrag = this.vertrag.value;
        request.typeOfAntrag = this.typeOfAntrag.value;
        request.aenderungsAntragBuergscheinNummer = this.aenderungsAntragBuergscheinNummer.value;
        request.auftraggeberTyp = this.auftraggeberTyp.value;
        request.auftraggeberName = this.auftraggeberName.value;
        request.auftraggeberStrasseNr = this.auftraggeberStrasseNr.value;
        request.auftraggeberPlz = this.auftraggeberPlz.value;
        request.auftraggeberOrt = this.auftraggeberOrt.value;
        request.auftraggeberLand = this.auftraggeberLand.value;
        request.vertretungsformel1 = this.vertretungsformel1.value;
        request.vertretungsformel2 = this.vertretungsformel2.value;
        request.vertretungsformel3 = this.vertretungsformel3.value;
        request.vertretungsformel4 = this.vertretungsformel4.value;
        request.vertretungsformel5 = this.vertretungsformel5.value;
        request.buergschaftsArt = this.buergschaftsArt.value;
        request.ausfuehrungsbuergschaftenDatum = this.ausfuehrungsbuergschaftenDatum.value;
        request.vertragserfuellungsbuergschaftenDatum = this.vertragserfuellungsbuergschaftenDatum.value;
        request.gewaehrleistungsbuergschaftenDatum = this.gewaehrleistungsbuergschaftenDatum.value;
        request.schlussrechnungsDatum = this.schlussrechnungsDatum.value;
        request.bietungsbuergschaftenDatum = this.bietungsbuergschaftenDatum.value;
        request.bauhandwerkersicherungssbuergschaftenDatum = this.bauhandwerkersicherungssbuergschaftenDatum.value;
        request.vorauszahlungsbuergschaftenDatum = this.vorauszahlungsbuergschaftenDatum.value;
        request.vertragsDatum = this.vertragsDatum.value;
        request.vertragsnummer = this.vertragsnummer.value;
        request.buergschaftshoehe = this.buergschaftshoehe.value;
        request.prozentSatz = this.prozentSatz.value;
        request.fristDatum = this.fristDatum.value;
        request.gesetzlGrundlage = this.gesetzlGrundlage.value;
        request.checkboxHatBesondereHinweise = !!this.checkboxHatBesondereHinweise.value;
        request.besondereHinweise = this.besondereHinweise.value;
        request.auftragsortGewerk = this.auftragsortGewerk.value;
        request.auftragsortVerfuegbar = this.auftragsortVerfuegbar.value;
        request.auftragsortBeschreibung = this.auftragsortBeschreibung.value;
        request.auftragsortStrasseNr = this.auftragsortStrasseNr.value;
        request.auftragsortPlz = this.auftragsortPlz.value;
        request.auftragsortOrt = this.auftragsortOrt.value;
        request.buergschaftsformat = this.buergschaftsformat.value;
        request.erklaerungsDatum = this.erklaerungsDatum.value;
        request.erklaerungsOrt = this.erklaerungsOrt.value;
        request.erklaerungsName = this.erklaerungsName.value;
        request.gewuenschtesFormular = this.gewuenschtesFormular.value;
        request.gewaehrleistungsfrist = this.gewaehrleistungsfrist.value;
        request.gewaehrleistungsfristJahre = this.gewaehrleistungsfristJahre.value;
        request.gewaehrleistungsfristMonate = this.gewaehrleistungsfristMonate.value;
        request.checkboxHinterlegung = !!this.checkboxHinterlegung.value;
        request.checkboxBGB768 = !!this.checkboxBGB768.value;
        request.checkboxSGBVII150Abs3 = !!this.checkboxSGBVII150Abs3.value;
        request.checkboxSGBIV28eAbs3a = !!this.checkboxSGBIV28eAbs3a.value;
        request.checkboxSicherheitseinbehalt = !!this.checkboxSicherheitseinbehalt.value;
        request.checkboxAentG14 = !!this.checkboxAentG14.value;
        request.checkboxErklaerungZustimmung = !!this.checkboxErklaerungZustimmung.value;
        request.roleName = this.roleName;
        request.activeUser = this.activeUser;
        request.user = this.user;
        request.files = this.files;

        request.auftraggeberSpKennung = this.auftraggeberSpKennung;

        // befristet = true, wenn befristet
        // befristet = false ansonsten, wenn unbefristet
        request.befristet = this.frist.value === 'befristet';

        if (this.templateSpKennung.value && !this.moechteAbweichendenBuergschaftstext) {
            // Digitale Bürgschaft (auch falls, abweichender Bürgschaftstext gewünscht)
            // -> Übertrage keine / ignoriere templateSpKennung für Trustlog
            request.templateSpKennung = this.templateSpKennung.value;

            // Bei digitaler Bürgschaft mappt der Server auf das entsprechende VHV-Formularkürzel; können wir hier nullen
            request.gewuenschtesFormular = null;
        } else {
            // Analoge Bürgschaft (auch falls, abweichender Bürgschaftstext gewünscht)
            // -> Übertrage keine / ignoriere templateSpKennung für Trustlog
            request.templateSpKennung = null;
        }

        // Wenn ein Schlussrechnungsdatum gesetzt ist, setze Gewährleistungsbürgschaftendatum zurück
        if (this.schlussrechnungsDatum.value && this.schlussrechnungsDatum.value.length > 0) {
            request.gewaehrleistungsbuergschaftenDatum = null;
        }

        // Wenn kein öffentlicher AG oder der Haken für Vertretungsformel nicht gesetzt ist,
        // übernehme doch nicht die Eingabe aus den Eingabefeldern
        if (this.shouldNotApplyVertretungsformel()) {
            request.vertretungsformel1 = null;
            request.vertretungsformel2 = null;
            request.vertretungsformel3 = null;
            request.vertretungsformel4 = null;
            request.vertretungsformel5 = null;
        }

        // Wenn die Versandart aufgrund anderer Konditionen nicht zur Auswahl steht, dann muss es eine Papierbürgschaft sein
        if (!this.versandartAuswahlAvailable) {
            request.buergschaftsformat = 'PAPIER';
        }

        return request;
    }

    public copyUserData(partnerResponse: PartnerProfilData, userData: UserData) {
        this.firma.value = partnerResponse.nachnameOderName1.value;
        this.plz.value = partnerResponse.anschriftAllgemein.value.plz;
        this.ort.value = partnerResponse.anschriftAllgemein.value.ort;
        this.street.value = partnerResponse.anschriftAllgemein.value.strasse;
        this.email.value = partnerResponse.email ? partnerResponse.email.value : userData.email.value;
        this.vorname.value = userData.name.value;
        this.nachname.value = userData.surname.value;
    }

    public copyData(
        buergscheinLight: BuergscheinLight,
        buergschein: Buergschein,
        vsnrFormatiert: string,
        buergscheinNummer: string = null,
        alter = false,
    ) {
        this.fillFieldValue(this.vertrag, PipeFunctions.formatVsnrRigid(vsnrFormatiert));

        this.copyDataAuftraggeber(buergschein);

        if (alter) {
            // Änderungsantrag
            this.typeOfAntrag.value = 'N';
            this.aenderungsAntragBuergscheinNummer.value = buergscheinNummer || buergscheinLight.buergscheinId;
            this.copyTemplate(buergschein);
            this.fillFieldValue(this.vertragsnummer, buergschein.geschaeftszeichen || '');
            this.fillFieldValue(this.vertragsDatum, buergschein.auftragsdatum || '');

            this.copyDataAuftrag(buergschein);

            this.fillFieldValue(this.gesetzlGrundlage, buergschein.rechtsGrundlage);

            // Wenn gültige Bürgschaftsart gesetzt diese übernehmen, ansonsten Feld frei lassen
            const buergschaftsArt = this.buergschaftsArt.regex.test(buergschein.buergschaftsArt)
                ? buergschein.buergschaftsArt
                : '';

            this.fillFieldValue(this.buergschaftsArt, buergschaftsArt);


            // Formatieren, Dezimaltrenner Punkt durch Komma ersetzen und danach die Tausendertrennzeichen entfernen
            this.fillFieldValue(
                this.buergschaftshoehe,
                buergschein.buergschaftsBetrag
                    .formatMoney(2, 3, '.', ',')
                    .replace('.', '')
            );

            // Befristung wiederherstellen
            const befrDate = buergschein.befristungsDatum;
            if (!befrDate) {
                this.frist.value = 'unbefristet';
            } else {
                this.frist.value = 'befristet';
                this.fillFieldValue(this.fristDatum, befrDate);
            }

            this.mapGwFrist(buergschein.gwFristMonate);
        } else {
            // Neuantrag
            this.typeOfAntrag.value = 'U';
        }

    }

    private mapGwFrist(gwFristMonate: number) {
        if (!gwFristMonate) {
            return;
        }

        const gwFristJahre = Math.floor(gwFristMonate / 12);
        const gwFristRestMonate = gwFristMonate % 12;

        if (!gwFristRestMonate && gwFristJahre === 4) {
            this.gewaehrleistungsfrist.value = '4';
            return;
        }

        if (!gwFristRestMonate && gwFristJahre === 5) {
            this.gewaehrleistungsfrist.value = '5';
            return;
        }

        this.gewaehrleistungsfrist.value = 'beträgt';

        if (gwFristJahre) {
            this.gewaehrleistungsfristJahre.value = gwFristJahre;
        }

        if (gwFristRestMonate) {
            this.gewaehrleistungsfristMonate.value = gwFristRestMonate;
        }
    }

    private copyDataAuftraggeber(buergschein: Buergschein) {
        if (buergschein.bspUnternehmensId) {
            // Digitale Bürgschaft
            this.isDigitaleBuergschaft = true;
            this.auftraggeberSpKennung = buergschein.bspUnternehmensId;
            this.templateSpKennung.value = buergschein.bspBuergschaftstextId;

            return;
        }

        // Analoge Bürgschaft
        if (CreateBuergscheinModel.hasAuftraggeberTypAsFirstLine(buergschein)) {
            let auftraggeberTyp = buergschein.auftraggeberinfo1 || '';
            auftraggeberTyp = auftraggeberTyp.replace('Firma/öffentl. Auftraggeber', 'Firma');

            // Wenn ein Auftraggebertyp gegeben ist, diesen wiederherstellen
            this.fillFieldValue(this.auftraggeberTyp, auftraggeberTyp);

            this.fillFieldValue(
                this.auftraggeberName,
                (buergschein.auftraggeberinfo2) || '' + (buergschein.auftraggeberinfo3 || '')
            );

            this.fillFieldValue(
                this.auftraggeberStrasseNr,
                (buergschein.auftraggeberinfo4 || '') + (buergschein.auftraggeberinfo5 || '')
            );
        } else {
            // Wenn kein Auftraggebertyp gegeben ist, fangen die Angaben schon in auftraggeberinfo1 an
            this.fillFieldValue(
                this.auftraggeberName,
                (buergschein.auftraggeberinfo1 || '') + (buergschein.auftraggeberinfo2 || '') + (buergschein.auftraggeberinfo3 || '')
            );

            this.fillFieldValue(
                this.auftraggeberStrasseNr,
                (buergschein.auftraggeberinfo4 || '') + (buergschein.auftraggeberinfo5 || '')
            );
        }

        this.copyDataAuftraggeberPlz(buergschein)
    }

    private copyDataAuftrag(buergschein: Buergschein) {
        // Es wird aus techn. Gründen nur Gewerk/Auftragsbeschreibung wiederhergestellt, nicht die anderen Auftragsortfelder.
        // Grund: Es ist nicht erkennbar, ob in auftrag5/6 nur eine Ortsbeschreibung oder Strasse, Nr, PLZ, Ort enthalten sind.
        const gewerk = (buergschein.auftrag1 || '')
                     + (buergschein.auftrag2 || '')
                     + (buergschein.auftrag3 || '')
                     + (buergschein.auftrag4 || '');

        this.fillFieldValue(this.auftragsortGewerk, gewerk);
    }

    private copyDataAuftraggeberPlz(buergschein: Buergschein) {
        const moeglichePlzFelder = [
            buergschein.auftraggeberinfo3,
            buergschein.auftraggeberinfo4,
            buergschein.auftraggeberinfo5,
            buergschein.auftraggeberinfo6,
        ];

        this.auftraggeberPlz.value = '';
        this.auftraggeberOrt.value = '';

        for (const feld of moeglichePlzFelder) {
            if (feld && CreateBuergscheinModel.PLZ_ORT_REGEX.test(feld)) {
                this.auftraggeberPlz.value = this.getPlzFromAuftraggeber(feld);
                this.auftraggeberOrt.value = this.getOrtFromAuftraggeber(feld);

                return;
            }
        }
    }

    private copyTemplate(buergschein: Buergschein) {
        if (buergschein.bspBuergschaftstextId) {
            // Digitale Bürgschaft
            this.fillFieldValue(this.templateSpKennung, buergschein.bspBuergschaftstextId);

            return;
        }

        // Analoge Bürgschaft
        this.fillFieldValue(this.gewuenschtesFormular, buergschein.formularArt || '');
    }

    // Index fuer die Position zwischen PLZ und Ort ermitteln
    private getIndexOfPlzOrtTrenner(plzOrtString: string): number {
        if (plzOrtString) {
            return plzOrtString.trim().indexOf(' ');
        }

        return -1;
    }

    // PLZ von Auftraggeberzeile ermitteln, sonst blank
    public getPlzFromAuftraggeber(auftraggeberZeile: string): string {
        if (this.getIndexOfPlzOrtTrenner(auftraggeberZeile) >= 0) {
            return auftraggeberZeile.substr(0, this.getIndexOfPlzOrtTrenner(auftraggeberZeile));
        }

        return '';
    }

    private shouldNotApplyVertretungsformel() {
        return this.auftraggeberTyp.value !== 'Öffentlicher Auftraggeber' || !this.vertretungsformelChecked;
    }

    private fillFieldValue(field: ValidatorInput, value: string) {
        // Setze Feldwert und triggere Validator
        field.value = value;
        this.validateField(field);
    }

    private static hasAuftraggeberTypAsFirstLine(buergschein: Buergschein): boolean {
        if (!buergschein.auftraggeberinfo1) {
            return false;
        }

        return this.AUFTRAGGEBER_TYPEN.includes(buergschein.auftraggeberinfo1.trim());
    }

}
